/**
 * The full lists behind the account screen's capped panels: `/admin/hosted`, `/admin/templates`,
 * `/admin/packs`. Questions have their own page (`/questions`), results theirs (`/games`).
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { api, type FormatSummary, type PackSummary, type SessionSummary } from '../lib/api';
import { authApi } from '../lib/auth';
import { Toast } from '../kit/Toast';
import { AuthGate } from '../components/AuthGate';
import { deleteTemplate, HostedList, PacksPanel, TemplatesPanel } from '../components/AccountPanels';

const TITLES = { hosted: 'Games I hosted', templates: 'My show templates', packs: 'My clue packs' } as const;
type ListName = keyof typeof TITLES;

export function AccountList() {
  const { list } = useParams();
  if (!list || !(list in TITLES)) return <Navigate to="/admin" replace />;
  const name = list as ListName;
  return (
    <AuthGate title={TITLES[name]}>
      <AccountListInner name={name} />
    </AuthGate>
  );
}

function AccountListInner({ name }: { name: ListName }) {
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [formats, setFormats] = useState<FormatSummary[] | null>(null);
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [gameNames, setGameNames] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const games = api.listGames().then((all) => {
      if (live) setGameNames(new Map(all.map((g) => [g.id, g.name])));
    });
    const rows =
      name === 'hosted'
        ? Promise.all([authApi.me(), api.listSessions()]).then(([me, all]) => {
            // The list also carries unowned seed shows; only this account's own are its history.
            if (live) setSessions(all.filter((s) => s.ownerAccountId === me.account?.id));
          })
        : name === 'templates'
          ? api.listFormats(true).then((all) => {
              if (live) setFormats(all);
            })
          : api.listPacks({ mine: true }).then((all) => {
              if (live) setPacks(all);
            });
    Promise.all([games, rows]).catch((err: unknown) => {
      if (live) setError(err instanceof Error ? err.message : 'Could not load the list.');
    });
    return () => {
      live = false;
    };
  }, [name]);

  function onDelete(format: FormatSummary) {
    void deleteTemplate(format, setError).then((gone) => {
      if (gone) setFormats((current) => current?.filter((f) => f.id !== format.id) ?? null);
    });
  }

  const loading = <p className="muted">Loading…</p>;
  return (
    <div className="admin">
      <div className="admin-header">
        <h1>{TITLES[name]}</h1>
        <span className="spacer" />
        <Link className="btn btn-sm" to="/admin">
          ← Account
        </Link>
      </div>
      {name === 'hosted' ? sessions ? <HostedList sessions={sessions} gameNames={gameNames} /> : loading : null}
      {name === 'templates' ? formats ? <TemplatesPanel formats={formats} onDelete={onDelete} /> : loading : null}
      {name === 'packs' ? packs ? <PacksPanel packs={packs} gameNames={gameNames} /> : loading : null}
      <Toast message={error} onDismiss={() => setError(null)} />
    </div>
  );
}
